import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  ArrowLeft,
  Star,
  ExternalLink,
  Loader2,
  TrendingUp,
  TrendingDown,
  Building2,
  Layers,
  Coins,
  Calendar,
  Activity,
  FileText,
  Newspaper,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";
import {
  fmtMoney,
  fmtPct,
  fmtScore,
  fmtVolume,
  fmtDate,
  fmtDateTime,
  recommendationColor,
} from "../lib/format";
import CandlestickChart from "../components/charts/CandlestickChart";
import VolumeChart from "../components/charts/VolumeChart";
import RsiChart from "../components/charts/RsiChart";
import MacdChart from "../components/charts/MacdChart";
import ReturnsChart from "../components/charts/ReturnsChart";
import ComparisonChart from "../components/charts/ComparisonChart";
import CustomTrendChart from "../components/charts/CustomTrendChart";
import ColorizationChart from "../components/charts/ColorizationChart";
import ScoreBreakdownModal from "../components/ScoreBreakdownModal";
import InsightModal from "../components/InsightModal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const PERIOD_OPTIONS = [
  { value: "90", label: "Últimos 3 meses", days: 90 },
  { value: "180", label: "Últimos 6 meses", days: 180 },
  { value: "365", label: "Últimos 12 meses", days: 365 },
  { value: "ALL", label: "Todo histórico", days: 100000 },
];

function StatBox({ label, value, sub, tone }) {
  const toneClass =
    tone === "buy" ? "text-buy" : tone === "sell" ? "text-sell" : "text-foreground";
  return (
    <div className="rounded-md border bg-card p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 font-mono-tab ${toneClass}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-border last:border-0">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <div className="text-sm font-medium text-right">{value}</div>
    </div>
  );
}

export default function StockDetail() {
  const { stockId } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [history, setHistory] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [news, setNews] = useState([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scoreOpen, setScoreOpen] = useState(false);
  const [insightOpen, setInsightOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("parte-1");
  const [part2Period, setPart2Period] = useState("ALL");

  // Shared filter state for Parte 1 charts (Tendência customizada + Colorização)
  const [p1Range, setP1Range] = useState("ALL");
  const [p1Field, setP1Field] = useState("close");
  const [p1M1Days, setP1M1Days] = useState(21);
  const [p1M2Days, setP1M2Days] = useState(63);

  // Slice history for Parte 2 charts based on the selected period
  const part2History = useMemo(() => {
    if (!history || history.length === 0) return [];
    const opt = PERIOD_OPTIONS.find((o) => o.value === part2Period);
    const days = opt ? opt.days : 100000;
    if (days >= history.length) return history;
    return history.slice(history.length - days);
  }, [history, part2Period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // /details é o único essencial — sem ele a página não tem o que mostrar.
      const d = await apiClient.get(`/stocks/${stockId}/details`);
      setDetails(d.data);

      // Os demais são opcionais — falhas (ex.: endpoint não deployado ainda)
      // não devem invalidar a página inteira.
      const [hRes, cRes, nRes] = await Promise.allSettled([
        apiClient.get(`/stocks/${stockId}/history`, { params: { days: 750 } }),
        apiClient.get(`/stocks/${stockId}/comparison`, { params: { days: 60 } }),
        apiClient.get(`/stocks/${stockId}/news`, { params: { limit: 3 } }),
      ]);
      setHistory(hRes.status === "fulfilled" ? hRes.value.data || [] : []);
      setComparison(cRes.status === "fulfilled" ? cRes.value.data || null : null);
      setNews(nRes.status === "fulfilled" ? nRes.value.data || [] : []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setDetails(null);
        toast.error("Ação não encontrada (ou backend ainda não atualizado).");
      } else {
        toast.error("Não foi possível carregar os dados da ação.");
      }
    } finally {
      setLoading(false);
    }
  }, [stockId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleFav = async () => {
    try {
      const r = await apiClient.post("/favorites/toggle", { stock_id: stockId });
      setDetails((d) => ({ ...d, is_favorite: r.data.is_favorite }));
      toast.success(r.data.is_favorite ? "Adicionado aos favoritos" : "Removido dos favoritos");
    } catch {
      toast.error("Falha ao atualizar favorito.");
    }
  };

  if (loading) {
    return (
      <div className="px-6 md:px-12 py-8 max-w-[1600px] mx-auto" data-testid="stock-detail-page">
        <div className="flex items-center gap-2 text-muted-foreground py-12">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando análise…
        </div>
      </div>
    );
  }

  if (!details || !details.stock) {
    return (
      <div className="px-6 md:px-12 py-8 max-w-[1600px] mx-auto">
        <div className="text-sm text-muted-foreground">Ação não encontrada.</div>
      </div>
    );
  }

  const { stock, last_quote, change_pct, composite_score, recommendation_label, usd_brl_reference_rate, is_favorite } = details;
  const isUp = (change_pct || 0) >= 0;
  const recClass = recommendationColor(recommendation_label);
  const officialUrl =
    stock.exchange === "B3"
      ? `https://dados.cvm.gov.br/dataset/cia_aberta-doc-cia-${stock.ticker.toLowerCase()}`
      : `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${stock.ticker}&type=10-K`;

  // Pseudo stock-card for modals
  const stockLite = {
    id: stock.id,
    ticker: stock.ticker,
    company_name: stock.company_name,
    exchange: stock.exchange,
    currency: stock.currency,
    sector: stock.sector,
  };

  return (
    <div className="px-6 md:px-12 py-8 max-w-[1600px] mx-auto" data-testid="stock-detail-page">
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="detail-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
            {stock.exchange} · {stock.sector} · {stock.industry}
          </div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-5xl font-bold tracking-tight leading-none" data-testid="detail-ticker">
              {stock.ticker}
            </h1>
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em]">
              {stock.exchange}
            </Badge>
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em]">
              {stock.currency}
            </Badge>
          </div>
          <div className="text-base text-muted-foreground mt-2">{stock.company_name}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleToggleFav}
            data-testid="detail-favorite-toggle"
            className={is_favorite ? "text-amber-500" : ""}
          >
            <Star className={`w-4 h-4 mr-2 ${is_favorite ? "fill-current" : ""}`} />
            {is_favorite ? "Favoritada" : "Favoritar"}
          </Button>
          <Button variant="outline" onClick={() => setScoreOpen(true)} data-testid="detail-open-score">
            <Activity className="w-4 h-4 mr-2" /> Transparência do score
          </Button>
          <Button onClick={() => setInsightOpen(true)} data-testid="detail-open-insight">
            <FileText className="w-4 h-4 mr-2" /> Nota analítica
          </Button>
        </div>
      </header>

      {/* Stats summary */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="detail-stats">
        <StatBox
          label="Fechamento"
          value={last_quote ? fmtMoney(last_quote.close, stock.currency) : "—"}
          sub={last_quote ? `Captura ${fmtDateTime(last_quote.captured_at)}` : null}
        />
        <StatBox
          label="Variação"
          value={fmtPct(change_pct)}
          sub={isUp ? "vs pregão anterior" : "vs pregão anterior"}
          tone={isUp ? "buy" : "sell"}
        />
        <StatBox label="Volume" value={last_quote ? fmtVolume(last_quote.volume) : "—"} />
        <StatBox
          label="Score Composto"
          value={composite_score != null ? fmtScore(composite_score) : "—"}
          tone={(composite_score || 0) >= 15 ? "buy" : (composite_score || 0) <= -15 ? "sell" : null}
        />
        <div className={`rounded-md border p-4 flex flex-col justify-center ${recClass}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-current opacity-80">Recomendação</div>
          <div className="font-display text-xl font-bold mt-1">{recommendation_label || "—"}</div>
          {usd_brl_reference_rate && (
            <div className="text-[10px] mt-1 opacity-70">USD/BRL ref. R$ {Number(usd_brl_reference_rate).toFixed(4)}</div>
          )}
        </div>
      </section>

      {/* Company info row */}
      <Card className="mb-8" data-testid="detail-company-card">
        <CardHeader>
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Empresa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1">
            <InfoRow icon={Building2} label="Razão social" value={stock.company_name} />
            <InfoRow icon={Layers} label="Setor" value={stock.sector} />
            <InfoRow icon={Layers} label="Indústria" value={stock.industry} />
            <InfoRow icon={Coins} label="Moeda" value={stock.currency} />
            <InfoRow icon={Calendar} label="Bolsa" value={stock.exchange} />
            <InfoRow icon={TrendingUp} label="Tipo" value={stock.company_type || "Common Stock"} />
            <InfoRow icon={Layers} label="Porte" value={stock.market_cap_tier || "—"} />
            <div className="flex items-center justify-between py-2.5">
              <a
                href={officialUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center text-xs underline-offset-4 hover:underline text-primary"
                data-testid="detail-official-source"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                {stock.exchange === "B3" ? "Documentos CVM" : "SEC EDGAR Filings"}
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============ TABS: Parte 1 / Parte 2 / Parte 3 ============ */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-10" data-testid="detail-tabs">
        <TabsList className="h-auto p-1 bg-muted/60 border border-border">
          <TabsTrigger value="parte-1" data-testid="tab-parte-1" className="px-4 py-2 text-sm">
            Parte 1 · Análise de tendência
          </TabsTrigger>
          <TabsTrigger value="parte-2" data-testid="tab-parte-2" className="px-4 py-2 text-sm">
            Parte 2 · Demais análises técnicas
          </TabsTrigger>
          <TabsTrigger value="parte-3" data-testid="tab-parte-3" className="px-4 py-2 text-sm">
            Parte 3 · Histórico completo
          </TabsTrigger>
        </TabsList>

        {/* ============ PARTE 1: Tendência customizada + últimas notícias ============ */}
        <TabsContent value="parte-1" className="mt-6" data-testid="detail-section-1">
        {history.length > 0 && (
          <Card className="mb-4" data-testid="detail-custom-trend">
            <CardHeader>
              <CardTitle className="font-display text-lg">
                Tendência customizada · preço × duas médias móveis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CustomTrendChart
                history={history}
                currency={stock.currency}
                stockId={stock.id}
                range={p1Range}
                setRange={setP1Range}
                field={p1Field}
                setField={setP1Field}
                m1Days={p1M1Days}
                setM1Days={setP1M1Days}
                m2Days={p1M2Days}
                setM2Days={setP1M2Days}
              />
            </CardContent>
          </Card>
        )}

        {history.length > 2 && (
          <Card className="mb-4" data-testid="detail-colorization">
            <CardHeader>
              <CardTitle className="font-display text-lg">
                Colorização customizada · regime do candle
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ColorizationChart
                history={history}
                currency={stock.currency}
                range={p1Range}
                field={p1Field}
                N={p1M1Days}
              />
            </CardContent>
          </Card>
        )}

        <Card data-testid="detail-latest-news">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Newspaper className="w-4 h-4" />
              Últimas 3 notícias sobre {stock.ticker}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {news.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                Sem notícias reais coletadas para este ativo no momento.
              </div>
            ) : (
              <div className="space-y-3">
                {news.map((n, idx) => (
                  <a
                    key={idx}
                    href={n.url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-md border p-3 hover:bg-accent transition"
                    data-testid={`news-item-${idx}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-[10px] uppercase tracking-[0.15em]">
                        {n.source_name || n.domain || "—"}
                      </Badge>
                      {n.is_renowned && (
                        <Badge variant="secondary" className="text-[10px] uppercase tracking-[0.15em]">
                          renomada
                        </Badge>
                      )}
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        {fmtDate(n.published_at)}
                      </span>
                    </div>
                    <div className="text-sm font-medium">{n.title}</div>
                    {n.description && (
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {n.description}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </TabsContent>

        {/* ============ PARTE 2: Demais gráficos (com filtro de período único) ============ */}
        <TabsContent value="parte-2" className="mt-6" data-testid="detail-section-2">
        <Card className="mb-6" data-testid="part2-period-card">
          <CardContent className="py-4 flex flex-wrap items-end gap-4">
            <div className="min-w-[220px]">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
                Período aplicado a todos os gráficos
              </div>
              <Select value={part2Period} onValueChange={setPart2Period}>
                <SelectTrigger
                  data-testid="part2-period-select"
                  className="filter-yellow"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-[11px] text-muted-foreground font-mono-tab">
              {part2History.length} pregões exibidos
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6" data-testid="detail-price-card">
          <CardHeader>
            <CardTitle className="font-display text-lg">Preço · candlestick com bandas</CardTitle>
          </CardHeader>
          <CardContent>
            {part2History.length > 2 ? (
              <>
                <CandlestickChart data={part2History} currency={stock.currency} />
                <div className="mt-6">
                  <VolumeChart data={part2History} />
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground py-8">
                Histórico indisponível para o período selecionado.
              </div>
            )}
          </CardContent>
        </Card>

        {part2History.length > 2 && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">RSI</CardTitle>
              </CardHeader>
              <CardContent><RsiChart data={part2History} /></CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">MACD</CardTitle>
              </CardHeader>
              <CardContent><MacdChart data={part2History} /></CardContent>
            </Card>
          </div>
        )}

        {part2History.length > 2 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-display text-lg">Retorno acumulado e drawdown</CardTitle>
            </CardHeader>
            <CardContent><ReturnsChart data={part2History} /></CardContent>
          </Card>
        )}

        {comparison && comparison.series && comparison.series.length > 1 && (
          <Card data-testid="detail-comparison">
            <CardHeader>
              <CardTitle className="font-display text-lg">
                Comparativo com pares de {stock.sector}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ComparisonChart series={comparison.series} />
              <div className="mt-3 flex flex-wrap gap-2">
                {comparison.series.map((s) => (
                  <Badge
                    key={s.ticker}
                    variant={s.is_main ? "default" : "outline"}
                    className="text-[10px] uppercase tracking-[0.15em]"
                  >
                    {s.ticker}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        </TabsContent>

        {/* ============ PARTE 3: Histórico completo paginado ============ */}
        <TabsContent value="parte-3" className="mt-6" data-testid="detail-section-3">
          {history.length > 0 ? (
            <PaginatedHistoryTable history={history} stock={stock} page={historyPage} setPage={setHistoryPage} />
          ) : (
            <div className="text-sm text-muted-foreground p-6">
              Nenhum histórico armazenado para esta ação.
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ScoreBreakdownModal open={scoreOpen} stock={stockLite} onClose={() => setScoreOpen(false)} />
      <InsightModal open={insightOpen} stock={stockLite} onClose={() => setInsightOpen(false)} />
    </div>
  );
}

function PaginatedHistoryTable({ history, stock, page, setPage }) {
  const pageSize = 50;
  // Desc order (newest first)
  const desc = useMemo(() => [...history].reverse(), [history]);
  const totalPages = Math.max(1, Math.ceil(desc.length / pageSize));
  const start = page * pageSize;
  const slice = desc.slice(start, start + pageSize);

  return (
    <Card data-testid="detail-history-table">
      <CardHeader>
        <CardTitle className="font-display text-lg flex items-center justify-between">
          <span>Histórico completo · {desc.length} pregões</span>
          <span className="text-xs text-muted-foreground font-normal font-mono-tab">
            Página {page + 1} de {totalPages}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Abertura</TableHead>
                <TableHead className="text-right">Máxima</TableHead>
                <TableHead className="text-right">Mínima</TableHead>
                <TableHead className="text-right">Fechamento</TableHead>
                <TableHead className="text-right">Ajustado</TableHead>
                <TableHead className="text-right">Volume</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slice.map((q, idx) => {
                const isUp = Number(q.close) >= Number(q.open);
                return (
                  <TableRow key={`${q.quote_date}-${idx}`}>
                    <TableCell>{fmtDate(q.quote_date)}</TableCell>
                    <TableCell className="text-right font-mono-tab">
                      {fmtMoney(q.open, stock.currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono-tab text-buy">
                      {fmtMoney(q.high, stock.currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono-tab text-sell">
                      {fmtMoney(q.low, stock.currency)}
                    </TableCell>
                    <TableCell
                      className={`text-right font-mono-tab font-semibold ${isUp ? "text-buy" : "text-sell"}`}
                    >
                      {fmtMoney(q.close, stock.currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono-tab">
                      {fmtMoney(q.adjusted_close, stock.currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono-tab">
                      {fmtVolume(q.volume)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <div className="text-xs text-muted-foreground">
            Mostrando {start + 1}-{Math.min(start + pageSize, desc.length)} de {desc.length}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              data-testid="history-prev"
            >
              <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(page + 1)}
              data-testid="history-next"
            >
              Próxima <ChevronRightIcon className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
